import { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

const GUIDE_USER = 'CauraTech';
const GUIDE_PASS = '7889CauraTech7.,.';

const SECTIONS = [
  {
    title: '1. Inicio de Sesion',
    img: '/guide-images/01_login.jpeg',
    desc: 'Accede a la plataforma con tu email y contrasena. Existen dos roles principales: Merchant (comercio) y Super Admin (administrador global).',
  },
  {
    title: '2. Dashboard del Merchant',
    img: '/guide-images/02_dashboard.jpeg',
    desc: 'Vista principal del comercio. Muestra estadisticas de ordenes, transacciones, volumen y comisiones del proyecto activo.',
  },
  {
    title: '3. Gestion de Proyectos',
    img: '/guide-images/03_projects.jpeg',
    desc: 'Cada comercio puede crear multiples proyectos. Cada proyecto tiene su propia configuracion, API Keys, ordenes y wallets.',
  },
  {
    title: '4. Webhooks',
    img: '/guide-images/04_webhooks.jpeg',
    desc: 'Configura webhooks para recibir notificaciones automaticas cuando una orden cambie de estado (pagada, expirada, cancelada). Puedes activar/desactivar, probar y ver historial de entregas.',
  },
  {
    title: '5. API Keys',
    img: '/guide-images/05_apikeys.jpeg',
    desc: 'Genera API Keys para conectar tu sistema con Caura Pay via API. Cada key esta asociada a un proyecto especifico. Necesitas al menos un proyecto creado para generar keys. Las keys se usan en el header X-API-Key de cada peticion.',
  },
  {
    title: '6. Documentacion API',
    img: '/guide-images/06_docs.jpeg',
    desc: 'Documentacion completa de la API v1 con ejemplos en cURL, JavaScript y Python. Puedes descargarla en formato .md o .txt para tu equipo.',
  },
  {
    title: '7. Pasarela de Pago Publica',
    img: '/guide-images/07_gateway.jpeg',
    desc: 'Cuando creas una orden via API, se genera un link de pago unico. El pagador accede a esta pagina donde puede elegir entre 4 metodos: Pago Movil (P2P), Debito Inmediato, Transferencia BCV y C2P.',
  },
  {
    title: '8. Panel Super Admin',
    img: '/guide-images/08_superadmin.jpeg',
    desc: 'El Super Admin tiene vision global de la plataforma: comercios, transferencias, wallets, ordenes, comisiones, credenciales y logs del sistema.',
  },
  {
    title: '9. Conexiones API (Super Admin)',
    img: '/guide-images/09_apiconn.jpeg',
    desc: 'Desde aqui el Super Admin puede ver todas las API Keys de los comercios, su estado (activa/revocada), el proyecto asociado y la fecha de ultimo uso.',
  },
  {
    title: '10. Configuracion de Produccion (Super Admin)',
    img: '/guide-images/10_production.jpeg',
    desc: 'Panel centralizado para gestionar todas las variables de entorno criticas: JWT, encriptacion, Banco Plaza (ambiente QA/produccion, API keys), CORS, URLs y conexion a base de datos. Los valores sensibles se muestran enmascarados. Cada cambio queda registrado en el log de auditoria.',
  },
];

function GuideContent() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#051C2C] text-white py-8">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-3xl font-bold">Guia de la Plataforma Caura Pay</h1>
          <p className="text-blue-200 mt-2">Documentacion interna con capturas de pantalla paso a paso</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">
        {/* Table of Contents */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">Contenido</h2>
          <div className="grid grid-cols-2 gap-2">
            {SECTIONS.map((s, i) => (
              <a key={i} href={`#section-${i}`} className="text-sm text-blue-600 hover:underline">{s.title}</a>
            ))}
            <a href="#credentials" className="text-sm text-blue-600 hover:underline">Usuarios de Prueba</a>
            <a href="#production" className="text-sm text-blue-600 hover:underline">Configuracion de Produccion</a>
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section, idx) => (
          <div key={idx} id={`section-${idx}`} className="bg-white border rounded-xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-2">{section.title}</h2>
              <p className="text-sm text-gray-600 mb-4">{section.desc}</p>
            </div>
            <img src={section.img} alt={section.title} className="w-full border-t" loading="lazy" />
          </div>
        ))}

        {/* Test Credentials */}
        <div id="credentials" className="bg-white border rounded-xl p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Usuarios de Prueba</h2>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Rol</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Password</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Acceso</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-4 py-3 font-medium">Super Admin</td>
                  <td className="px-4 py-3 font-mono text-blue-700">superadmin@cauratech.com</td>
                  <td className="px-4 py-3 font-mono">SuperAdmin123!</td>
                  <td className="px-4 py-3 text-xs text-gray-500">Panel global: merchants, transferencias, analytics, credenciales, conexiones API</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Merchant (Admin)</td>
                  <td className="px-4 py-3 font-mono text-blue-700">admin@cauratech.com</td>
                  <td className="px-4 py-3 font-mono">admin123</td>
                  <td className="px-4 py-3 text-xs text-gray-500">Dashboard, proyectos, ordenes, wallets, API Keys, webhooks, documentacion</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Production Configuration */}
        <div id="production" className="bg-white border rounded-xl p-6 space-y-6">
          <h2 className="text-xl font-bold text-gray-800">Configuracion para Produccion</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-900">Todos estos cambios se pueden hacer directamente desde el panel de Super Admin</p>
            <p className="text-xs text-blue-700 mt-1">Ingresa como Super Admin → Pestaña "Produccion" → Modifica los valores → Guardar Cambios. Los valores sensibles se muestran enmascarados por seguridad.</p>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">1. Cambiar a Ambiente de Produccion (Banco Plaza)</h3>
            <p className="text-sm text-gray-600 mb-2">Desde Super Admin → Produccion → Seccion "Banco Plaza":</p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Cambiar <code className="bg-gray-100 px-1 rounded text-xs">BANCO_PLAZA_ENVIRONMENT</code> de "test" a <strong>"production"</strong></li>
              <li>Ingresar las credenciales de produccion: <code className="bg-gray-100 px-1 rounded text-xs">BANCO_PLAZA_API_KEY</code> y <code className="bg-gray-100 px-1 rounded text-xs">BANCO_PLAZA_API_SECRET</code></li>
              <li>Verificar que <code className="bg-gray-100 px-1 rounded text-xs">BANCO_PLAZA_BASE_URL_PROD</code> sea <strong>https://api1.bancoplaza.com:8282</strong></li>
              <li>Guardar y reiniciar el servidor</li>
            </ol>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">2. Seguridad JWT y Encriptacion</h3>
            <p className="text-sm text-gray-600 mb-2">Desde Super Admin → Produccion → Seccion "Seguridad JWT" y "Encriptacion":</p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Generar un nuevo <code className="bg-gray-100 px-1 rounded text-xs">JWT_SECRET_KEY</code> unico (min 32 caracteres aleatorios)</li>
              <li>Generar un nuevo <code className="bg-gray-100 px-1 rounded text-xs">ENCRYPTION_KEY</code> (se puede generar con: <code className="bg-gray-100 px-1 rounded text-xs">python3 -c "from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())"</code>)</li>
            </ol>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">3. CORS y URLs de Produccion</h3>
            <p className="text-sm text-gray-600 mb-2">Desde Super Admin → Produccion → Seccion "CORS y URLs":</p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Cambiar <code className="bg-gray-100 px-1 rounded text-xs">CORS_ORIGINS</code> de "*" a <strong>"https://caurapay.com"</strong></li>
              <li>Cambiar <code className="bg-gray-100 px-1 rounded text-xs">FRONTEND_URL</code> a <strong>"https://caurapay.com"</strong></li>
            </ol>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">4. Conexion Directa a Base de Datos</h3>
            <p className="text-sm text-gray-600 mb-2">Desde Super Admin → Produccion → Seccion "Base de Datos":</p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Obtener la Connection String desde Supabase Dashboard → Settings → Database → Connection Pooling</li>
              <li>Ingresar en <code className="bg-gray-100 px-1 rounded text-xs">DATABASE_URL</code></li>
              <li>Reiniciar. El indicador cambiara de "REST API (fallback)" a "Conexion Directa (asyncpg)"</li>
            </ol>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-700 mb-2">5. Whitelist de IPs en Banco Plaza</h3>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Obtener las IPs de salida estaticas del servidor de produccion</li>
              <li>Enviar a Banco Plaza junto con el RIF de la empresa</li>
              <li>Esperar confirmacion de activacion</li>
            </ol>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-red-900">Checklist antes de ir a produccion:</p>
            <ul className="list-disc list-inside text-sm text-red-800 space-y-1 mt-2">
              <li>JWT_SECRET_KEY cambiado (no usar el de pruebas)</li>
              <li>ENCRYPTION_KEY cambiado</li>
              <li>Contrasenas de usuarios de prueba cambiadas</li>
              <li>CORS_ORIGINS configurado con dominio de produccion</li>
              <li>BANCO_PLAZA_ENVIRONMENT en "production"</li>
              <li>Credenciales de Banco Plaza de produccion ingresadas</li>
              <li>IPs de produccion en whitelist de Banco Plaza</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlatformGuide() {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (user === GUIDE_USER && pass === GUIDE_PASS) {
      setAuthenticated(true);
      setError('');
    } else {
      setError('Credenciales incorrectas');
    }
  };

  if (authenticated) return <GuideContent />;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #051C2C 0%, #003594 50%, #008AD8 100%)' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold" style={{ color: '#051C2C' }}>Guia de Plataforma</h1>
          <p className="text-sm text-gray-500">Acceso restringido</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg text-center">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <input type="text" value={user} onChange={e => setUser(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contrasena</label>
            <div className="relative">
              <input type={showPass ? 'text' : 'password'} value={pass} onChange={e => setPass(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10" required />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" className="w-full py-2.5 text-white font-medium rounded-lg" style={{ backgroundColor: '#003594' }}>Acceder</button>
        </form>
      </div>
    </div>
  );
}
